import React, { useCallback, useMemo, useRef } from 'react';
import { Highlight } from 'react-pdf-highlighter-extended';

interface CitationTimelineProps {
  highlights: Array<Highlight>;
  selectedHighlightId: string;
  setSelectedHighlightId: (id: string) => void;
  currentPage: number;
  pageCount: number;
  onPageChange?: (page: number) => void;
}

export const CitationTimeline: React.FC<CitationTimelineProps> = ({
  highlights,
  selectedHighlightId,
  setSelectedHighlightId,
  currentPage,
  pageCount,
  onPageChange,
}) => {
  const timelineRef = useRef<HTMLDivElement>(null);
  const isDraggingRef = useRef(false);

  // Create a list of pages that have at least one citation
  const pagesWithCitations = useMemo(() => {
    const pageMap = new Map<number, number>();
    highlights.forEach((h) => {
      const pageNum = h.position.boundingRect.pageNumber;
      pageMap.set(pageNum, (pageMap.get(pageNum) || 0) + 1);
    });
    return Array.from(pageMap.entries())
      .map(([pageNumber, citationCount]) => ({ pageNumber, citationCount }))
      .sort((a, b) => a.pageNumber - b.pageNumber);
  }, [highlights]);

  // Helper: given a page number, return the left offset (percentage) on the bar
  const pageToPercent = useCallback(
    (pageNumber: number) => {
      if (pageCount <= 0) return 0;
      const step = 100 / pageCount;
      return step * (pageNumber - 0.5);
    },
    [pageCount],
  );

  // Helper: given a percentage, return the page number
  const percentToPage = useCallback(
    (percent: number) => {
      if (pageCount <= 0) return 1;
      const step = 100 / pageCount;
      const approximate = percent / step + 0.5; // reverse of pageToPercent
      const page = Math.round(approximate);
      return Math.max(1, Math.min(pageCount, page));
    },
    [pageCount],
  );

  // Handler when marker clicked
  const handleMarkerClick = (pageNumber: number) => {
    // Find first highlight on that page
    const highlightOnPage = highlights.find(
      (h) => h.position.boundingRect.pageNumber === pageNumber,
    );
    if (highlightOnPage) {
      setSelectedHighlightId(highlightOnPage.id);
    }
  };

  const computePageFromClientX = useCallback(
    (clientX: number) => {
      if (!timelineRef.current) return currentPage;
      const rect = timelineRef.current.getBoundingClientRect();
      const clampedX = Math.max(0, Math.min(rect.width, clientX - rect.left));
      const percent = (clampedX / rect.width) * 100;
      return percentToPage(percent);
    },
    [currentPage, percentToPage],
  );

  const handlePointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    if (!onPageChange) return;
    isDraggingRef.current = true;
    const targetPage = computePageFromClientX(e.clientX);
    onPageChange(targetPage);

    // Attach listeners to document to handle drag outside element bounds
    const handlePointerMove = (ev: PointerEvent) => {
      if (!isDraggingRef.current) return;
      const page = computePageFromClientX(ev.clientX);
      onPageChange(page);
    };

    const handlePointerUp = () => {
      isDraggingRef.current = false;
      document.removeEventListener('pointermove', handlePointerMove);
      document.removeEventListener('pointerup', handlePointerUp);
    };

    document.addEventListener('pointermove', handlePointerMove);
    document.addEventListener('pointerup', handlePointerUp);
  };

  // Backward compatibility: still support simple click
  const handleTimelineClick = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      if (!onPageChange) return;
      const targetPage = computePageFromClientX(e.clientX);
      onPageChange(targetPage);
    },
    [computePageFromClientX, onPageChange],
  );

  const handleMarkerKeyDown = (
    e: React.KeyboardEvent<HTMLButtonElement>,
    pageNumber: number,
  ) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleMarkerClick(pageNumber);
    }
  };

  return (
    <div className="relative w-full h-20 flex items-center px-4 select-none bg-white border-b border-gray-200">
      {/* Base progress line container */}
      <div className="absolute left-4 right-16 top-1/2 -translate-y-1/2 h-2">
        <div
          ref={timelineRef}
          className="relative w-full h-full bg-gray-300 rounded cursor-pointer hover:bg-gray-400 transition-colors"
          onClick={handleTimelineClick}
          onPointerDown={handlePointerDown}
          role="slider"
          aria-label="PDF page scrubber"
          aria-valuenow={currentPage}
          aria-valuemin={1}
          aria-valuemax={pageCount}
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === 'ArrowLeft' && currentPage > 1 && onPageChange) {
              onPageChange(currentPage - 1);
            } else if (
              e.key === 'ArrowRight' &&
              currentPage < pageCount &&
              onPageChange
            ) {
              onPageChange(currentPage + 1);
            }
          }}
        >
          {/* Current page indicator */}
          {pageCount > 0 && (
            <div
              className="absolute left-0 top-1/2 -translate-y-1/2"
              style={{ left: `${pageToPercent(currentPage)}%` }}
            >
              {/* vertical line */}
              <div
                className="relative w-0.5 bg-blue-500"
                style={{ height: '28px', marginLeft: '-1px' }}
              />
              {/* bubble above line */}
              <div className="absolute left-1/2 -translate-x-1/2 -top-6 bg-blue-500 text-white text-xs px-1.5 py-0.5 rounded-md font-medium shadow-sm">
                {currentPage}
              </div>
            </div>
          )}

          {/* Citation markers */}
          {pagesWithCitations.map(({ pageNumber, citationCount }) => {
            const isSelected = highlights.some(
              (h) =>
                h.position.boundingRect.pageNumber === pageNumber &&
                h.id === selectedHighlightId,
            );

            return (
              <div
                key={pageNumber}
                className="absolute left-0 top-1/2 -translate-y-1/2"
                style={{ left: `${pageToPercent(pageNumber)}%` }}
              >
                {/* dot */}
                <button
                  type="button"
                  tabIndex={0}
                  aria-label={`Page ${pageNumber} citation marker - ${citationCount} citation${citationCount === 1 ? '' : 's'}`}
                  onClick={() => handleMarkerClick(pageNumber)}
                  onKeyDown={(e) => handleMarkerKeyDown(e, pageNumber)}
                  className="focus:outline-none focus:ring-2 focus:ring-blue-300 rounded-full relative -translate-x-1/2"
                >
                  <span
                    className={`block rounded-full transition-all duration-150 ${
                      isSelected
                        ? 'h-4 w-4 bg-orange-600 shadow-md'
                        : 'h-3 w-3 bg-gray-600 hover:bg-gray-700'
                    }`}
                  />
                </button>
                {/* bubble above */}
                <div
                  className={`absolute -translate-x-1/2  text-xs px-1 py-0.5 rounded-md font-medium transition-all duration-150 ${
                    isSelected
                      ? 'bg-orange-500 text-white shadow-sm'
                      : 'bg-gray-600 text-white'
                  }`}
                  style={{ marginBottom: '4px' }}
                >
                  {pageNumber}
                </div>
                {citationCount > 1 && (
                  <div
                    className="absolute  -translate-x-1/2 -translate-y-12 text-xs text-gray-500 mt-1 font-medium"
                    style={{ top: '100%' }}
                  >
                    {citationCount}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Total page count on the right */}
      <div className="absolute right-4 top-1/2 -translate-y-1/2 bg-gray-100 px-2.5 py-0.5 rounded-md text-xs font-medium text-gray-700">
        {pageCount}
      </div>
    </div>
  );
};
